import { ComboBox } from './ComboBox'
import { ASPECT_RATIOS, AspectRatioKey, DesignChannels } from 'components/NewFileModal/Types'
import { Section } from '@react-stately/collections'
import { TextChannelKey, TurnInto } from 'types'
import { TextChannels } from '../../../../../../../constants'
import { useModals } from 'almanac-ui/Modal'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { GroupComboBoxProps } from 'screens/blaze/Home/components/Catalogue/components/GroupComboBox/Types'
import { Wizard } from 'components/Wizards/Wizard'
import { schemas } from 'components/Wizards/schemas/schemas'
import { TurnIntoWizard } from 'components/Wizards/TurnInto/TurnIntoWizard'
import { SourceType } from 'components/Wizards/TurnInto/Source/Types'
import { SEOPlanningWizard } from 'components/Wizards/SEOPlanningWizard/SEOPlanningWizard'
import { YoutubeScriptWizard } from 'components/Wizards/YoutubeScriptWizard/YoutubeScriptWizard'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useCreateDesign } from 'screens/blaze/Home/components/Catalogue/components/useCreateDesign'
import { Group } from 'components/Wizards/Catalogue/Types'
import { useFeatureFlaggedGroups } from 'screens/blaze/Home/components/Catalogue/useFeatureFlaggedGroups'
import { useSearchGroups } from 'components/Wizards/Catalogue/useGroupSearch'
import { useState } from 'react'
import Styles from './GroupComboBox.module.scss'
import cx from 'classnames'
import { BlogPostWizard } from 'components/Wizards/BlogPostWizard/BlogPostWizard'
import { multiStepSchemas } from 'components/Wizards/schemas/multiStepSchemas'
import { MultiStepWizard } from 'components/Wizards/MultiStepWizard'
import { MultiStepInstagramPlanningWizard } from 'components/Wizards/InstagramPlanning/MultiStepInstagramPlanningWizard'
import { MultiStepFacebookPlanningWizard } from 'components/Wizards/FacebookPlanning/MultiStepFacebookPlanningWizard'
import { MultiStepTwitterPlanningWizard } from 'components/Wizards/TwitterPlanning/MultiStepTwitterPlanningWizard'
import { MultiStepBlogPostWizard } from 'components/Wizards/MultiStepBlogPostWizard/MultiStepBlogPostWizard'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { Values } from 'components/Wizards/MultiStepBlogPostWizard/Types'
import { MultiStepLinkedInPlanningWizard } from 'components/Wizards/LinkedInPlanning/MultiStepLinkedInPlanningWizard'
import { ContentPlanWizard } from 'components/Wizards/ContentPlanWizard/ContentPlanWizard'
import { MultiStepContentPlanSchema } from 'components/Wizards/schemas/ContentPlanSchema'
import { EmailNewsletterMultiStepSchema } from 'components/Wizards/schemas/EmailNewsletter'
import { MultiStepEmailNewsletterWizard } from 'components/Wizards/MultiStepEmailNewsletterWizard/MultiStepEmailNewsletterWizard'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { Item } from 'components/Menu/MenuItem'
import { useNeedsUpgradeForChannelType } from 'hooks/useChannelNeedsUpgrade'
import { TierUpgradeModal } from 'components/TierUpgradeModal/TierUpgradeModal'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { DesktopWizardProps, FlowKey, FlowWizards } from 'components/Wizards/DesktopWizards/Types'
import { desktopDesignSchemas } from 'components/Wizards/schemas/desktopSchemas'
import { FlowA2 } from 'components/Wizards/DesktopWizards/Flows/FlowA2/FlowA2'
import { FlowB } from 'components/Wizards/DesktopWizards/Flows/FlowB/FlowB'
import { FlowC } from 'components/Wizards/DesktopWizards/Flows/FlowC/FlowC'

type MenuKey = `${string}:${TextChannelKey | AspectRatioKey}`

export const GroupComboBox = ({ initialValues, groups, ...props }: GroupComboBoxProps) => {
  const { openModal } = useModals()
  const { createDesign } = useCreateDesign()
  const { shouldForceUpgrade, currentTierPlan, nextTierPlan } = useNeedsUpgradeForChannelType()

  const { data: brandKits } = useBrandKits()
  const latestBrandKitId = brandKits?.[brandKits.length - 1]?.id

  const openWizard = (contentTypeKey: ChannelType) => {
    const newsletterMultistepSchema = multiStepSchemas[contentTypeKey as ChannelType]
    if (newsletterMultistepSchema) {
      openModal(MultiStepWizard, { initialValues, schema: newsletterMultistepSchema }, { underlayVariant: 'wizard' })
      return
    }
    openModal(Wizard, { initialValues, schema: schemas[contentTypeKey as ChannelType] }, { underlayVariant: 'wizard' })
  }

  const multiplePostOpener = (key: TextChannelKey, wizardOpener: () => void) => {
    if (shouldForceUpgrade(key) && nextTierPlan && currentTierPlan) {
      return openModal(TierUpgradeModal, {
        reason: 'feature_disabled',
        feature: 'Multiple Post Wizards',
        upgradeTier: nextTierPlan,
        currentTier: currentTierPlan,
        onUpgrade: wizardOpener,
      })
    }

    return wizardOpener()
  }

  const extractContentTypeKey = (compositeKey: MenuKey): TextChannelKey | AspectRatioKey => {
    return compositeKey.split(':')[1] as TextChannelKey | AspectRatioKey
  }

  const handleSelectionChange = (key: MenuKey) => {
    const contentTypeKey = extractContentTypeKey(key)

    const isWizardChannel = Object.keys(TextChannels).includes(contentTypeKey as TextChannelKey)
    const isDesignChannel = Object.keys(ASPECT_RATIOS).includes(contentTypeKey as AspectRatioKey)
    const flow = FlowWizards[contentTypeKey as AspectRatioKey]

    if (flow) {
      const contentType = DesignChannels[contentTypeKey as AspectRatioKey]
      const schema = desktopDesignSchemas[contentType]
      const { width, height } = ASPECT_RATIOS[contentTypeKey as AspectRatioKey]

      const params = {
        schema: schema,
        initialValues: {
          width,
          height,
          ...initialValues,
        },
      } as DesktopWizardProps

      if (schema) {
        if (flow === FlowKey.A2) {
          openModal(FlowA2, params)
          return
        } else if (flow === FlowKey.B) {
          openModal(FlowB, params)
          return
        } else if (flow === FlowKey.C) {
          openModal(FlowC, params)
          return
        }
      }
    }

    if (isWizardChannel) {
      switch (contentTypeKey) {
        case ChannelType.BlogPost:
          let blogPostMultistepSchema = multiStepSchemas[ChannelType.BlogPost] as
            | MultiStepSchema<ChannelType.BlogPost>
            | undefined

          if (blogPostMultistepSchema) {
            openModal(
              MultiStepBlogPostWizard,
              { initialValues: initialValues as Values, schema: blogPostMultistepSchema },
              { underlayVariant: 'wizard' },
            )
            return
          }
          openModal(
            BlogPostWizard,
            { initialValues, schema: schemas[contentTypeKey as ChannelType] },
            { underlayVariant: 'wizard' },
          )
          return
        case ChannelType.YoutubeScript:
          openModal(YoutubeScriptWizard, { initialValues }, { underlayVariant: 'wizard' })
          return
        case ChannelType.Newsletter:
          openModal(
            MultiStepEmailNewsletterWizard,
            {
              schema: EmailNewsletterMultiStepSchema,
              initialValues: { brand_kit_id: latestBrandKitId, ...initialValues } as Values,
            },
            { underlayVariant: 'wizard' },
          )
          return
        case GenerationType.TwitterPlanning:
          multiplePostOpener(contentTypeKey, () =>
            openModal(MultiStepTwitterPlanningWizard, { initialValues }, { underlayVariant: 'wizard' }),
          )
          return
        case GenerationType.SEOPlanning:
          multiplePostOpener(contentTypeKey, () =>
            openModal(SEOPlanningWizard, { initialValues }, { underlayVariant: 'wizard' }),
          )
          return
        case GenerationType.FacebookPlanning:
          multiplePostOpener(contentTypeKey, () =>
            openModal(MultiStepFacebookPlanningWizard, { initialValues }, { underlayVariant: 'wizard' }),
          )
          return
        case GenerationType.InstagramPlanning:
          multiplePostOpener(contentTypeKey, () =>
            openModal(MultiStepInstagramPlanningWizard, { initialValues }, { underlayVariant: 'wizard' }),
          )
          return
        case GenerationType.LinkedInPlanning:
          multiplePostOpener(contentTypeKey, () =>
            openModal(MultiStepLinkedInPlanningWizard, { initialValues }, { underlayVariant: 'wizard' }),
          )
          return
        case GenerationType.Campaign:
          openModal(TurnIntoWizard, { initialValues, initialIsCampaign: true }, { underlayVariant: 'wizard' })
          return
        case GenerationType.ContentPlan:
          openModal(
            ContentPlanWizard,
            { initialValues, schema: MultiStepContentPlanSchema },
            { underlayVariant: 'wizard' },
          )
          return
        case TurnInto.DocsIntoContent:
          openModal(TurnIntoWizard, { initialValues, sourceType: SourceType.File }, { underlayVariant: 'wizard' })
          return
        case TurnInto.WebsitesIntoContent:
          openModal(TurnIntoWizard, { initialValues, sourceType: SourceType.Website }, { underlayVariant: 'wizard' })
          return
        case TurnInto.AudioIntoContent:
          openModal(TurnIntoWizard, { initialValues, sourceType: SourceType.AudioFile }, { underlayVariant: 'wizard' })
          return
        case TurnInto.VideoIntoContent:
          openModal(TurnIntoWizard, { initialValues, sourceType: SourceType.VideoFile }, { underlayVariant: 'wizard' })
          return
        default:
          openWizard(contentTypeKey as ChannelType)
          return
      }
    }

    if (isDesignChannel) {
      createDesign({ aspectRatioKey: contentTypeKey as AspectRatioKey, ...initialValues })
      return
    }
  }

  const guardedHandleSelectionChange = usePaywallGuard(handleSelectionChange)

  return (
    <ComboBox
      size="lg"
      menuTrigger="focus"
      allowsCustomValue={false}
      placeholder="What would you like to create?"
      onSelectionChange={(key) => {
        if (!key) return
        guardedHandleSelectionChange(key as MenuKey)
      }}
      aria-label="What would you like to create?"
      items={groups}
      {...props}
    >
      {(group: Group) => {
        return (
          <Section key={group.name} title={group.name} items={group.channels}>
            {group.channels.map((item) => {
              const itemKey = `${group.name}:${item}`
              const isAspectRatio = Object.keys(ASPECT_RATIOS).includes(item as AspectRatioKey)
              if (isAspectRatio) {
                const aspectRatio = ASPECT_RATIOS[item as AspectRatioKey]
                const { name, icon: Icon, width, height } = aspectRatio

                return (
                  <Item key={itemKey} textValue={name}>
                    <Icon size={20} />
                    <div className={Styles.label}>{name}</div>
                    <span className={Styles.description}>
                      {width}x{height}
                    </span>
                  </Item>
                )
              }

              const textChannel = TextChannels[item as TextChannelKey]
              const { name, icon: Icon } = textChannel
              return (
                <Item key={itemKey} textValue={name}>
                  <Icon size={20} />
                  <div className={Styles.label}>{name}</div>
                  <span className={Styles.description}>Document</span>
                </Item>
              )
            })}
          </Section>
        )
      }}
    </ComboBox>
  )
}

export const GroupComboBoxContainer = ({
  initialValues,
  withPlusIcon = false,
  ...props
}: Omit<GroupComboBoxProps, 'groups' | 'onInputChange'>) => {
  const groups = useFeatureFlaggedGroups()
  const searchGroups = useSearchGroups()
  const [search, setSearch] = useState('')
  const filteredGroups = searchGroups(groups, search || '')
  const [isFocused, setFocused] = useState(false)

  return (
    <div
      className={cx(Styles.container, {
        [Styles.focused]: isFocused,
      })}
    >
      <GroupComboBox
        withPlusIcon={withPlusIcon}
        contentsClassName={withPlusIcon ? Styles.homeV3Contents : ''}
        initialValues={initialValues}
        groups={filteredGroups}
        onInputChange={(value) => setSearch(value)}
        inputValue={search}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...props}
      />
    </div>
  )
}
